import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import AutoScanImg from "../../Assets/AutoScan.png";
import { FiArrowUpRight, FiMoreVertical } from "react-icons/fi";
import firstStepImg from "../../Assets/box.png";
import secondStepImg from "../../Assets/location.png";
import thirdStepImg from "../../Assets/mapLocation.png";
import fourthStepImg from "../../Assets/bus.png";
import fifthStepImg from "../../Assets/msg.png";

const UnifiedCloudSolutions = () => {
  const tabs = [
    { title: "Supply Chain", subtitle: "Planning", active: false },
    { title: "Supply Chain", subtitle: "Execution", active: false },
    { title: "Supply Chain", subtitle: "Tracking", active: true },
    { title: "Supply Chain", subtitle: "Planning", active: false },
  ];

  const inventorySteps = [
    { img: firstStepImg },
    { img: secondStepImg },
    { img: thirdStepImg },
    { img: fourthStepImg },
    { img: fifthStepImg },
  ];

  // Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const tabVariants = {
    rest: { scale: 1, y: 0 },
    hover: { scale: 1.04, y: -6, transition: { duration: 0.3 } },
  };

  const cardTiltVariants = {
    rest: { rotate: 0, scale: 1 },
    hover: { rotate: 0, scale: 1.04, transition: { duration: 0.4 } },
  };

  
  // Word stagger for key phrases
  const wordVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.08, duration: 0.5 },
    }),
  };

  const splitWords = (text) =>
    text.split(" ").map((word, i) => (
      <motion.span
        key={i}
        custom={i}
        variants={wordVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="inline-block mr-1"
      >
        {word}
      </motion.span>
    ));

  return (
    <motion.div
      className="px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-4 sm:py-6 md:py-8 lg:py-10 xl:py-12"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={containerVariants}
    >
      <div className="mx-auto">
        {/* Header Section */}
        <motion.div
          className="text-center mb-1 sm:mb-2 md:mb-3 lg:mb-4 xl:mb-5 px-2 sm:px-0"
          variants={itemVariants}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-brandDark mb-2 sm:mb-3">
            Simplify{" "}
            <span className="text-brandBlue font-normal">Challenges</span> With
          </h2>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-brandDark leading-tight sm:leading-tight md:leading-tight">
            Unified Cloud Solutions
          </h1>
        </motion.div>

        {/* Tabs Section */}
        <motion.div
          className="bg-white rounded-[20px] sm:rounded-[30px] md:rounded-[40px] p-4 sm:p-5 md:p-6 shadow-[0_4px_30px_rgba(0,0,0,0.05)] border border-gray-100 flex flex-wrap justify-center lg:justify-between items-stretch gap-4 sm:gap-6 mb-8 sm:mb-16 md:mb-20 lg:mb-24"
          variants={itemVariants}
        >
          {tabs.map((tab, index) => (
            <motion.div
              key={index}
              className={`
                group flex-1 min-w-[calc(50%-12px)] sm:min-w-[calc(50%-16px)] md:min-w-[calc(50%-20px)]
                lg:min-w-0 lg:flex-1 max-w-[280px]
                p-4 sm:p-5 md:p-6 rounded-2xl sm:rounded-3xl cursor-pointer
                ${
                  tab.active
                    ? "bg-brandOrange text-white border-brandOrange shadow-[0_10px_30px_rgba(232,81,20,0.35)] -translate-y-1"
                    : "bg-gray-100 text-gray-800 border-gray-200"
                }
                transition-all duration-300 ease-in-out
                hover:bg-brandOrange hover:text-white hover:border-brandOrange
                hover:shadow-[0_10px_30px_rgba(232,81,20,0.35)] hover:-translate-y-1
              `}
              variants={tabVariants}
              initial="rest"
              whileHover="hover"
              whileTap={{ scale: 0.96 }}
            >
              <div className="text-sm sm:text-base md:text-lg lg:text-xl font-medium mb-1">
                {tab.title}
              </div>
              <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">
                {tab.subtitle}
              </div>
              <div className="flex items-center text-xs sm:text-sm md:text-base opacity-70 group-hover:opacity-100 transition-opacity">
                <span>Below Here</span>
                <FiArrowUpRight
                  className="ml-1 sm:ml-2 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  size="1.1em"
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Cards Content */}
        <div className="space-y-8 lg:space-y-12 px-0 sm:px-2 md:px-4 lg:px-8 xl:px-12">
          {/* Top Row: Small Cards */}
          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 relative items-start">
            {/* Card 1: Incident Response Time - Tilted Left */}
            <div className="flex justify-center md:justify-start transform md:-translate-y-4 lg:-translate-y-8">
              <motion.div
                className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-gray-50 relative w-full max-w-[280px] sm:max-w-[300px] md:max-w-[320px] h-auto min-h-[120px] sm:min-h-[140px] md:min-h-[160px] transform md:-rotate-12 lg:-rotate-12 transition-transform duration-300 hover:rotate-0 hover:scale-105 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]"
                whileHover={{ rotate: 0, scale: 1.05, y: -4 }}
                transition={{ duration: 0.4 }}
              >
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600">
                  <FiMoreVertical size="1.2em" />
                </div>
                <p className="text-gray-400 text-[10px] sm:text-xs md:text-sm mb-1 sm:mb-2 font-medium">
                  Real-time analytics
                </p>
                <p className="text-gray-400 text-[10px] sm:text-xs md:text-sm mb-2 sm:mb-3 font-medium">
                  Incident Response Time
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">
                    125.46
                  </span>
                  <span className="text-[#10b981] flex items-center text-[10px] sm:text-xs md:text-sm">
                    <FiArrowUpRight className="inline mr-0.5" /> 15.23%
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Card 2: Compliance check - Tilted Right */}
            <div className="flex justify-center transform md:translate-y-4 lg:translate-y-8">
              <motion.div
                className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-gray-50 relative w-full max-w-[320px] sm:max-w-[340px] md:max-w-[360px] h-auto min-h-[140px] sm:min-h-[160px] md:min-h-[180px] transform md:rotate-6 lg:rotate-6 transition-transform duration-300 hover:rotate-0 hover:scale-105 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]"
                whileHover={{ rotate: 0, scale: 1.05, y: -4 }}
                transition={{ duration: 0.4 }}
              >
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600">
                  <FiMoreVertical size="1.2em" />
                </div>
                <p className="text-brandDark font-bold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">
                  Compliance check in progress
                </p>
                <p className="text-gray-400 text-[9px] sm:text-[10px] md:text-xs mb-3 sm:mb-4">
                  Viewing 24h report
                </p>
                <div className="relative w-full h-2 sm:h-2.5 md:h-3 lg:h-3.5 bg-gray-100 rounded-full overflow-hidden mb-1 sm:mb-2">
                  <motion.div
                    className="absolute left-0 top-0 h-full bg-[#10b981] rounded-full transition-all duration-500"
                    initial={{ width: "0%" }}
                    whileInView={{ width: "54%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, delay: 0.3 }}
                  />
                </div>
                <div className="text-right text-[9px] sm:text-[10px] md:text-xs text-gray-400 font-bold">
                  54%
                </div>
              </motion.div>
            </div>

            {/* Card 3: Impact of Risk - Tilted Left */}
            <div className="flex justify-center md:justify-end transform md:-translate-y-4 lg:-translate-y-8">
              <motion.div
                className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-gray-50 relative w-full max-w-[320px] sm:max-w-[340px] md:max-w-[360px] h-auto min-h-[140px] sm:min-h-[160px] md:min-h-[180px] transform md:-rotate-12 lg:-rotate-12 transition-transform duration-300 hover:rotate-0 hover:scale-105 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]"
                whileHover={{ rotate: 0, scale: 1.05, y: -4 }}
                transition={{ duration: 0.4 }}
              >
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600">
                  <FiMoreVertical size="1.2em" />
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row justify-between items-start gap-2 sm:gap-4">
                  <div className="flex-1">
                    <p className="text-[#1D1D1D] font-bold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">
                      Impact of Risk
                    </p>
                    <p className="text-gray-400 text-[9px] sm:text-[10px] md:text-xs mb-2 sm:mb-3">
                      Viewing 24h report
                    </p>
                  </div>
                  <div className="flex gap-1.5 sm:gap-2 md:gap-3 border-2 border-dashed border-gray-100 p-1.5 sm:p-2 md:p-2.5 rounded-lg">
                    <div className="flex flex-col items-center">
                      <span className="text-[7px] sm:text-[8px] md:text-[9px] text-gray-400 mb-1 leading-none text-center">
                        Data
                        <br />
                        Leakage
                      </span>
                      <div className="w-2.5 sm:w-3 md:w-3.5 h-2.5 sm:h-3 md:h-3.5 rounded-full border-2 border-red-500 bg-red-100 flex items-center justify-center">
                        <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-red-500"></div>
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-[7px] sm:text-[8px] md:text-[9px] text-gray-400 mb-1 text-center">
                        Malware
                      </span>
                      <div className="w-5 sm:w-6 md:w-7 h-5 sm:h-6 md:h-7 rounded-full border-2 border-red-500 bg-red-100 flex items-center justify-center">
                        <div className="w-1.5 sm:w-2 md:w-2.5 h-1.5 sm:h-2 md:h-2.5 rounded-full bg-red-500"></div>
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-[7px] sm:text-[8px] md:text-[9px] text-gray-400 mb-1 leading-none text-center">
                        Weak
                        <br />
                        Passwords
                      </span>
                      <div className="flex gap-[1px] sm:gap-0.5">
                        <div className="w-3.5 sm:w-4 md:w-4.5 h-3.5 sm:h-4 md:h-4.5 rounded-full border-2 border-red-500 bg-red-100"></div>
                        <div className="w-1.5 sm:w-2 md:w-2.5 h-1.5 sm:h-2 md:h-2.5 rounded-full border-2 border-red-500 bg-red-100 self-end"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Bottom Row: Large Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch pt-6 lg:pt-10">
            {/* Card 4 - AI Market Growth */}
            <motion.div
              className="lg:col-span-6 xl:col-span-3 p-4 sm:p-5 md:p-6 lg:p-7 rounded-2xl shadow-md border border-gray-50 flex flex-col justify-between transition-transform duration-300 hover:shadow-lg"
              variants={itemVariants}
              whileHover={{ scale: 1.03, y: -8 }}
            >
              <div>
                <p className="text-[9px] sm:text-[10px] md:text-xs lg:text-sm text-gray-400 uppercase font-bold tracking-widest mb-2 sm:mb-3 md:mb-4">
                  Real-time analytics
                </p>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-brandDark mb-4 sm:mb-5 md:mb-6 lg:mb-7 leading-relaxed">
                  {splitWords("AI market is expected to grow by")}
                  <span className="text-brandBlue font-bold"> 59% </span>
                  {splitWords("in 2025")}
                </h3>
              </div>

              <div className="relative h-20 sm:h-24 md:h-28 lg:h-32 mb-4 sm:mb-5 md:mb-6 lg:mb-7">
                <svg
                  className="w-full h-full overflow-visible"
                  viewBox="0 0 100 60"
                >
                  {[0, 15, 30, 45, 60].map((y) => (
                    <line
                      key={y}
                      x1="0"
                      y1={y}
                      x2="100"
                      y2={y}
                      stroke="#f0f0f0"
                      strokeWidth="0.5"
                    />
                  ))}
                  <motion.path
                    d="M0,50 L25,55 L50,35 L75,45 L100,55"
                    fill="none"
                    stroke="#00A3FF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.8, ease: "easeOut", delay: 0.5 }}
                  />
                  <circle
                    cx="50"
                    cy="35"
                    r="4"
                    fill="#00A3FF"
                    className="animate-pulse"
                  />
                  <circle cx="50" cy="35" r="2" fill="white" />
                  <g transform="translate(42, 23)">
                    <rect width="18" height="8" rx="4" fill="#00A3FF" />
                    <text
                      x="9"
                      y="5.5"
                      textAnchor="middle"
                      fontSize="4"
                      fill="white"
                      fontWeight="bold"
                    >
                      59% Growth
                    </text>
                  </g>
                </svg>
                <div className="flex justify-between text-[7px] sm:text-[8px] md:text-[9px] text-gray-300 mt-1 sm:mt-2 px-1">
                  <span>2020</span>
                  <span>2021</span>
                  <span>2022</span>
                  <span>2023</span>
                  <span>2025</span>
                </div>
              </div>

              <div>
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#1D1D1D] mb-0.5">
                  $30B
                </p>
                <p className="text-gray-400 text-[9px] sm:text-[10px] md:text-xs font-semibold">
                  Expected Valuation
                </p>
              </div>
            </motion.div>

            {/* Card 5 - AutoScan Image */}
            <motion.div
              className="lg:col-span-6 xl:col-span-3 overflow-hidden relative h-full"
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.4 }}
              whileHover={{ scale: 1.03 }}
            >
              <img
                src={AutoScanImg}
                alt="Automation Scanning"
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Card 6 - Inventory Monitoring */}
            <motion.div
              className="lg:col-span-12 xl:col-span-6 bg-white p-4 sm:p-5 md:p-6 lg:p-7 rounded-[25px] sm:rounded-[30px] md:rounded-[35px] lg:rounded-[40px] shadow-[0_10px_50px_rgba(0,0,0,0.05)] border border-gray-50 flex flex-col justify-between transition-transform duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.1)]"
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -6 }}
            >
              <p className="text-sm sm:text-base md:text-lg lg:text-xl w-full text-brandDark mb-4 sm:mb-5 md:mb-6 lg:mb-7 font-semibold leading-relaxed sm:leading-relaxed md:leading-relaxed">
                {splitWords("Monitor Your Inventory Levels In")}
                <span className="text-brandCyan font-bold"> Real-Time</span>,
                {splitWords(
                  "Ensuring You Never Run Out Of Stock Unexpectedly.",
                )}
              </p>

              {/* Icon Flow */}
              <div className="grid grid-cols-3 md:flex md:flex-nowrap items-center justify-between mb-4 sm:mb-6 md:mb-8 lg:mb-10 px-0 sm:px-2 md:px-4 relative gap-y-6 gap-x-2 md:gap-4">
                {inventorySteps.map((step, i) => (
                  <React.Fragment key={i}>
                    <motion.div
                      className="flex flex-col items-center justify-center relative w-full"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15 + 0.3, type: "spring" }}
                    >
                      <motion.div
                        className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 flex items-center justify-center relative z-10"
                        whileHover={{ scale: 1.15, rotate: 8 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <img
                          src={step.img}
                          alt={`Step ${i + 1}`}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </motion.div>

                      {/* Dotted Line Connector */}
                      {i < inventorySteps.length - 1 && (
                        <div
                          className={`absolute left-[70%] top-1/2 -translate-y-1/2 w-full h-[2px] hidden ${
                            i === 2 ? "md:flex" : "sm:flex md:flex"
                          }`}
                        >
                          <motion.div
                            className="h-full w-full border-t-2 border-dotted border-gray-200 origin-left"
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: 1 }}
                            viewport={{ once: true }}
                            transition={{
                              duration: 0.8,
                              delay: i * 0.2 + 0.5,
                              ease: "easeOut",
                            }}
                          />
                        </div>
                      )}
                    </motion.div>
                  </React.Fragment>
                ))}
              </div>

              {/* Status Gauges - animated fill simulation */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-6 mt-2 sm:mt-4">
                {[
                  { color: "#00F0FF", label: "Succeed" },
                  { color: "#1A9FFF", label: "In Progress" },
                  { color: "#A18F93", label: "Failed", opacity: 0.6 },
                ].map((gauge, i) => (
                  <div
                    key={i}
                    className="space-y-1 sm:space-y-1.5 md:space-y-2"
                  >
                    <div className="flex justify-between items-center text-[9px] sm:text-[10px] md:text-xs lg:text-sm font-bold text-gray-400">
                      <span>{gauge.label}</span>
                      <span>%</span>
                    </div>
                    <div
                      className={`h-2 sm:h-2.5 md:h-3 lg:h-3.5 bg-gray-200 rounded-full shadow-inner relative overflow-hidden opacity-${gauge.opacity || 100}`}
                    >
                      <motion.div
                        className={`absolute left-0 top-0 h-full rounded-full bg-[${gauge.color}]`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${65 - i * 20}%` }} // example values: 65%, 45%, 25%
                        viewport={{ once: true }}
                        transition={{
                          duration: 1.6,
                          delay: 0.6 + i * 0.2,
                          ease: "easeOut",
                        }}
                      >
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 w-0.5 sm:w-1 h-0.5 sm:h-1 rounded-full bg-white opacity-80"></div>
                      </motion.div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UnifiedCloudSolutions;

// import React from "react";
// import AutoScanImg from "../../Assets/AutoScan.png";
// import { FiArrowUpRight, FiMoreVertical } from "react-icons/fi";
// import firstStepImg from "../../Assets/box.png";
// import secondStepImg from "../../Assets/location.png";
// import thirdStepImg from "../../Assets/mapLocation.png";
// import fourthStepImg from "../../Assets/bus.png";
// import fifthStepImg from "../../Assets/msg.png";

// const UnifiedCloudSolutions = () => {
//   const tabs = [
//     { title: "Supply Chain", subtitle: "Planning", active: false },
//     { title: "Supply Chain", subtitle: "Execution", active: false },
//     { title: "Supply Chain", subtitle: "Tracking", active: true },
//     { title: "Supply Chain", subtitle: "Planning", active: false },
//   ];

//   const inventorySteps = [
//     { img: firstStepImg },
//     { img: secondStepImg },
//     { img: thirdStepImg },
//     { img: fourthStepImg },
//     { img: fifthStepImg },
//   ];

//   return (
//     <div className="px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-4 sm:py-6 md:py-8 lg:py-10 xl:py-12">
//       <div className="mx-auto">
//         {/* Header Section */}
//         <div className="text-center mb-1 sm:mb-2 md:mb-3 lg:mb-4 xl:mb-5 px-2 sm:px-0">
//           <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-brandDark mb-2 sm:mb-3">
//             Simplify{" "}
//             <span className="text-brandBlue font-normal">Challenges</span> With
//           </h2>
//           <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-brandDark leading-tight sm:leading-tight md:leading-tight">
//             Unified Cloud Solutions
//           </h1>
//         </div>

//         {/* Tabs Section */}
//         <div
//           className="bg-white rounded-[20px] sm:rounded-[30px] md:rounded-[40px]
//   p-4 sm:p-5 md:p-6
//   shadow-[0_4px_30px_rgba(0,0,0,0.05)]
//   border border-gray-100
//   flex flex-wrap justify-center lg:justify-between
//   items-stretch gap-4 sm:gap-6
//   mb-8 sm:mb-16 md:mb-20 lg:mb-24"
//         >
//           {tabs.map((tab, index) => (
//             <div
//               key={index}
//               className="
//         group
//         flex-1 min-w-[calc(50%-12px)] sm:min-w-[calc(50%-16px)] md:min-w-[calc(50%-20px)]
//         lg:min-w-0 lg:flex-1 max-w-[280px]
//         p-4 sm:p-5 md:p-6
//         rounded-2xl sm:rounded-3xl
//         cursor-pointer
//         bg-gray-100 text-gray-800 border border-gray-200
//         transition-all duration-300 ease-in-out
//         hover:bg-brandOrange hover:text-white hover:border-brandOrange
//         hover:shadow-[0_10px_30px_rgba(232,81,20,0.35)]
//         hover:-translate-y-1
//       "
//             >
//               <div className="text-sm sm:text-base md:text-lg lg:text-xl font-medium mb-1">
//                 {tab.title}
//               </div>

//               <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">
//                 {tab.subtitle}
//               </div>

//               <div className="flex items-center text-xs sm:text-sm md:text-base opacity-70 group-hover:opacity-100 transition-opacity">
//                 <span>Below Here</span>
//                 <FiArrowUpRight
//                   className="ml-1 sm:ml-2 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
//                   size="1.1em"
//                 />
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* Cards Content */}
//         <div className="space-y-8 lg:space-y-12 px-0 sm:px-2 md:px-4 lg:px-8 xl:px-12">
//           {/* Top Row: Small Cards */}
//           <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 relative items-start">
//             {/* Card 1: Incident Response Time - Tilted Left */}
//             <div className="flex justify-center md:justify-start transform md:-translate-y-4 lg:-translate-y-8">
//               <div className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-gray-50 relative w-full max-w-[280px] sm:max-w-[300px] md:max-w-[320px] h-auto min-h-[120px] sm:min-h-[140px] md:min-h-[160px] transform md:-rotate-12 lg:-rotate-12 transition-transform duration-300 hover:rotate-0 hover:scale-105 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
//                 <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600">
//                   <FiMoreVertical size="1.2em" />
//                 </div>
//                 <p className="text-gray-400 text-[10px] sm:text-xs md:text-sm mb-1 sm:mb-2 font-medium">
//                   Real-time analytics
//                 </p>
//                 <p className="text-gray-400 text-[10px] sm:text-xs md:text-sm mb-2 sm:mb-3 font-medium">
//                   Incident Response Time
//                 </p>
//                 <div className="flex items-baseline gap-2">
//                   <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">
//                     125.46
//                   </span>
//                   <span className="text-[#10b981] flex items-center text-[10px] sm:text-xs md:text-sm">
//                     <FiArrowUpRight className="inline mr-0.5" /> 15.23%
//                   </span>
//                 </div>
//               </div>
//             </div>

//             {/* Card 2: Compliance check - Tilted Right */}
//             <div className="flex justify-center transform md:translate-y-4 lg:translate-y-8">
//               <div className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-gray-50 relative w-full max-w-[320px] sm:max-w-[340px] md:max-w-[360px] h-auto min-h-[140px] sm:min-h-[160px] md:min-h-[180px] transform md:rotate-6 lg:rotate-6 transition-transform duration-300 hover:rotate-0 hover:scale-105 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
//                 <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600">
//                   <FiMoreVertical size="1.2em" />
//                 </div>
//                 <p className="text-brandDark font-bold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">
//                   Compliance check in progress
//                 </p>
//                 <p className="text-gray-400 text-[9px] sm:text-[10px] md:text-xs mb-3 sm:mb-4">
//                   Viewing 24h report
//                 </p>
//                 <div className="relative w-full h-2 sm:h-2.5 bg-gray-100 rounded-full overflow-hidden mb-1 sm:mb-2">
//                   <div
//                     className="absolute left-0 top-0 h-full bg-[#10b981] rounded-full transition-all duration-500"
//                     style={{ width: "54%" }}
//                   ></div>
//                 </div>
//                 <div className="text-right text-[9px] sm:text-[10px] md:text-xs text-gray-400 font-bold">
//                   54%
//                 </div>
//               </div>
//             </div>

//             {/* Card 3: Impact of Risk - Tilted Left */}
//             <div className="flex justify-center md:justify-end transform md:-translate-y-4 lg:-translate-y-8">
//               <div className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-gray-50 relative w-full max-w-[320px] sm:max-w-[340px] md:max-w-[360px] h-auto min-h-[140px] sm:min-h-[160px] md:min-h-[180px] transform md:-rotate-12 lg:-rotate-12 transition-transform duration-300 hover:rotate-0 hover:scale-105 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
//                 <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600">
//                   <FiMoreVertical size="1.2em" />
//                 </div>
//                 <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row justify-between items-start gap-2 sm:gap-4">
//                   <div className="flex-1">
//                     <p className="text-[#1D1D1D] font-bold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">
//                       Impact of Risk
//                     </p>
//                     <p className="text-gray-400 text-[9px] sm:text-[10px] md:text-xs mb-2 sm:mb-3">
//                       Viewing 24h report
//                     </p>
//                   </div>
//                   <div className="flex gap-1.5 sm:gap-2 md:gap-3 border-2 border-dashed border-gray-100 p-1.5 sm:p-2 md:p-2.5 rounded-lg">
//                     <div className="flex flex-col items-center">
//                       <span className="text-[7px] sm:text-[8px] md:text-[9px] text-gray-400 mb-1 leading-none text-center">
//                         Data
//                         <br />
//                         Leakage
//                       </span>
//                       <div className="w-2.5 sm:w-3 md:w-3.5 h-2.5 sm:h-3 md:h-3.5 rounded-full border-2 border-red-500 bg-red-100 flex items-center justify-center">
//                         <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-red-500"></div>
//                       </div>
//                     </div>
//                     <div className="flex flex-col items-center">
//                       <span className="text-[7px] sm:text-[8px] md:text-[9px] text-gray-400 mb-1 text-center">
//                         Malware
//                       </span>
//                       <div className="w-5 sm:w-6 md:w-7 h-5 sm:h-6 md:h-7 rounded-full border-2 border-red-500 bg-red-100 flex items-center justify-center">
//                         <div className="w-1.5 sm:w-2 md:w-2.5 h-1.5 sm:h-2 md:h-2.5 rounded-full bg-red-500"></div>
//                       </div>
//                     </div>
//                     <div className="flex flex-col items-center">
//                       <span className="text-[7px] sm:text-[8px] md:text-[9px] text-gray-400 mb-1 leading-none text-center">
//                         Weak
//                         <br />
//                         Passwords
//                       </span>
//                       <div className="flex gap-[1px] sm:gap-0.5">
//                         <div className="w-3.5 sm:w-4 md:w-4.5 h-3.5 sm:h-4 md:h-4.5 rounded-full border-2 border-red-500 bg-red-100"></div>
//                         <div className="w-1.5 sm:w-2 md:w-2.5 h-1.5 sm:h-2 md:h-2.5 rounded-full border-2 border-red-500 bg-red-100 self-end"></div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Bottom Row: Large Cards */}
//           <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch pt-6 lg:pt-10">
//             {/* Card 4 */}
//             <div className="lg:col-span-6 xl:col-span-3 p-4 sm:p-5 md:p-6 lg:p-7 rounded-2xl shadow-md border border-gray-50 flex flex-col justify-between transition-transform duration-300 hover:scale-105 hover:shadow-lg">
//               <div>
//                 <p className="text-[9px] sm:text-[10px] md:text-xs lg:text-sm text-gray-400 uppercase font-bold tracking-widest mb-2 sm:mb-3 md:mb-4">
//                   Real-time analytics
//                 </p>
//                 <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-brandDark mb-4 sm:mb-5 md:mb-6 lg:mb-7 leading-relaxed">
//                   AI market is expected to grow by{" "}
//                   <span className="text-brandBlue font-bold">59%</span> in 2025
//                 </h3>
//               </div>

//               <div className="relative h-20 sm:h-24 md:h-28 lg:h-32 mb-4 sm:mb-5 md:mb-6 lg:mb-7">
//                 <svg
//                   className="w-full h-full overflow-visible"
//                   viewBox="0 0 100 60"
//                 >
//                   {[0, 15, 30, 45, 60].map((y) => (
//                     <line
//                       key={y}
//                       x1="0"
//                       y1={y}
//                       x2="100"
//                       y2={y}
//                       stroke="#f0f0f0"
//                       strokeWidth="0.5"
//                     />
//                   ))}
//                   <path
//                     d="M0,50 L25,55 L50,35 L75,45 L100,55"
//                     fill="none"
//                     stroke="#00A3FF"
//                     strokeWidth="2.5"
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                   />
//                   <circle
//                     cx="50"
//                     cy="35"
//                     r="4"
//                     fill="#00A3FF"
//                     className="animate-pulse"
//                   />
//                   <circle cx="50" cy="35" r="2" fill="white" />
//                   <g transform="translate(42, 23)">
//                     <rect width="18" height="8" rx="4" fill="#00A3FF" />
//                     <text
//                       x="9"
//                       y="5.5"
//                       textAnchor="middle"
//                       fontSize="4"
//                       fill="white"
//                       fontWeight="bold"
//                     >
//                       59% Growth
//                     </text>
//                   </g>
//                 </svg>
//                 <div className="flex justify-between text-[7px] sm:text-[8px] md:text-[9px] text-gray-300 mt-1 sm:mt-2 px-1">
//                   <span>2020</span>
//                   <span>2021</span>
//                   <span>2022</span>
//                   <span>2023</span>
//                   <span>2025</span>
//                 </div>
//               </div>

//               <div>
//                 <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#1D1D1D] mb-0.5">
//                   $30B
//                 </p>
//                 <p className="text-gray-400 text-[9px] sm:text-[10px] md:text-xs font-semibold">
//                   Expected Valuation
//                 </p>
//               </div>
//             </div>

//             {/* Card 5 (Image card – NO forced height change) */}
//             <div className="lg:col-span-6 xl:col-span-3 overflow-hidden relative h-full">
//               <img
//                 src={AutoScanImg}
//                 alt="Automation Scanning"
//                 className="w-full h-full object-cover"
//               />
//             </div>

//             {/* Card 6 */}
//             <div className="lg:col-span-12 xl:col-span-6 bg-white p-4 sm:p-5 md:p-6 lg:p-7 rounded-[25px] sm:rounded-[30px] md:rounded-[35px] lg:rounded-[40px] shadow-[0_10px_50px_rgba(0,0,0,0.05)] border border-gray-50 flex flex-col justify-between transition-transform duration-300 hover:scale-105 hover:shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
//               <p className="text-sm sm:text-base md:text-lg lg:text-xl w-full text-brandDark mb-4 sm:mb-5 md:mb-6 lg:mb-7 font-semibold leading-relaxed sm:leading-relaxed md:leading-relaxed">
//                 Monitor Your Inventory Levels In{" "}
//                 <span className="text-brandCyan font-bold">Real-Time</span>,
//                 Ensuring You Never Run Out Of Stock Unexpectedly.
//               </p>

//               {/* Icon Flow */}
//               <div className="grid grid-cols-3 md:flex md:flex-nowrap items-center justify-between mb-4 sm:mb-6 md:mb-8 lg:mb-10 px-0 sm:px-2 md:px-4 relative gap-y-6 gap-x-2 md:gap-4">
//                 {inventorySteps.map((step, i) => (
//                   <React.Fragment key={i}>
//                     <div className="flex flex-col items-center justify-center relative w-full">
//                       <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 flex items-center justify-center relative z-10 transition-transform duration-300 hover:scale-110">
//                         <img
//                           src={step.img}
//                           alt={`Step ${i + 1}`}
//                           className="w-full h-full object-contain"
//                           loading="lazy"
//                         />
//                       </div>

//                       {/* Dotted Line - Connector */}
//                       {i < inventorySteps.length - 1 && (
//                         <div
//                           className={`absolute left-[70%] top-1/2 -translate-y-1/2 w-full h-[2px] hidden
//                           ${i === 2 ? "md:flex" : "sm:flex md:flex"}
//                         `}
//                         >
//                           <div className="h-full w-full border-t-2 border-dotted border-gray-200"></div>
//                         </div>
//                       )}
//                     </div>
//                   </React.Fragment>
//                 ))}
//               </div>

//               {/* Status Gauges */}
//               <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-6 mt-2 sm:mt-4">
//                 <div className="space-y-1 sm:space-y-1.5 md:space-y-2">
//                   <div className="flex justify-between items-center text-[9px] sm:text-[10px] md:text-xs lg:text-sm font-bold text-gray-400">
//                     <span>Succeed</span>
//                     <span>%</span>
//                   </div>
//                   <div className="h-2 sm:h-2.5 md:h-3 lg:h-3.5 bg-[#00F0FF] rounded-full shadow-inner relative overflow-hidden">
//                     <div className="absolute right-1 top-1/2 -translate-y-1/2 w-0.5 sm:w-1 h-0.5 sm:h-1 rounded-full bg-white opacity-80"></div>
//                   </div>
//                 </div>
//                 <div className="space-y-1 sm:space-y-1.5 md:space-y-2">
//                   <div className="flex justify-between items-center text-[9px] sm:text-[10px] md:text-xs lg:text-sm font-bold text-gray-400">
//                     <span>In Progress</span>
//                     <span>%</span>
//                   </div>
//                   <div className="h-2 sm:h-2.5 md:h-3 lg:h-3.5 bg-[#1A9FFF] rounded-full shadow-inner relative overflow-hidden">
//                     <div className="absolute right-1 top-1/2 -translate-y-1/2 w-0.5 sm:w-1 h-0.5 sm:h-1 rounded-full bg-white opacity-80"></div>
//                   </div>
//                 </div>
//                 <div className="space-y-1 sm:space-y-1.5 md:space-y-2">
//                   <div className="flex justify-between items-center text-[9px] sm:text-[10px] md:text-xs lg:text-sm font-bold text-gray-400">
//                     <span>Failed</span>
//                     <span>%</span>
//                   </div>
//                   <div className="h-2 sm:h-2.5 md:h-3 lg:h-3.5 bg-[#A18F93] rounded-full shadow-inner relative overflow-hidden opacity-60">
//                     <div className="absolute right-1 top-1/2 -translate-y-1/2 w-0.5 sm:w-1 h-0.5 sm:h-1 rounded-full bg-white opacity-80"></div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default UnifiedCloudSolutions;
